import { useMemo, useState } from 'react';

import type { CreateSupplierWithBranch } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { useCoa } from '../../lib/queries/coa';
import { useCreateSupplier } from '../../lib/queries/suppliers';
import { AppModal } from '../AppModal';
import { SearchSelect } from '../SearchSelect';

export interface SupplierFormModalProps {
  companyId: string;
  // RD-sourced prefill (M2-e wires this); undefined => blank manual create.
  prefill?: Partial<{
    taxId: string;
    nameTh: string;
    branchCode: string;
    branchName: string;
    addressTh: string;
  }>;
  onClose: () => void;
  onCreated?: (supplierId: string) => void;
}

// Label class reconciled to the app's real convention (`meta`) — the plan named
// a `fld-label` class that does not exist in the codebase.
const labelClass = 'meta';
const labelStyle = { display: 'block', marginBottom: 6 } as const;

export function SupplierFormModal({
  companyId,
  prefill,
  onClose,
  onCreated,
}: SupplierFormModalProps) {
  const { data: coa } = useCoa(companyId, true);
  const create = useCreateSupplier(companyId);

  const [nameTh, setNameTh] = useState(prefill?.nameTh ?? '');
  const [taxId, setTaxId] = useState(prefill?.taxId ?? '');
  const [defaultExpenseCode, setDefaultExpenseCode] = useState('');
  const [terms, setTerms] = useState('');
  const [branchCode, setBranchCode] = useState(prefill?.branchCode ?? '00000');
  const [branchName, setBranchName] = useState(prefill?.branchName ?? 'สำนักงานใหญ่');
  const [addressTh, setAddressTh] = useState(prefill?.addressTh ?? '');

  const expenseOptions = useMemo(
    () =>
      (coa ?? [])
        .filter((c) => c.type === 'EXPENSE' && c.isActive && !c.isControl)
        .map((c) => ({ value: c.code, label: `${c.code} · ${c.nameTh}` })),
    [coa],
  );
  const expenseLabel = useMemo(
    () => new Map(expenseOptions.map((o) => [o.value, o.label])),
    [expenseOptions],
  );

  async function handleSubmit() {
    // The create body is `CreateSupplierWithBranch` — FLAT entity fields + a nested
    // `branch`. companyId comes from the URL (the hook scopes the request) and both
    // `code`s are auto-generated server-side, so neither appears in the body.
    // currency/country/isActive are required on both entity + branch; the first
    // branch carries the entity name.
    const body: CreateSupplierWithBranch = {
      nameTh,
      ...(taxId ? { taxId } : {}),
      ...(defaultExpenseCode ? { defaultExpenseCode } : {}),
      ...(terms ? { terms } : {}),
      currency: 'THB',
      country: 'TH',
      isActive: true,
      branch: {
        nameTh,
        branchCode,
        ...(branchName ? { branchName } : {}),
        ...(addressTh ? { addressTh } : {}),
        currency: 'THB',
        country: 'TH',
        isActive: true,
      },
    };
    const created = await create.mutateAsync(body);
    // CreateSupplierResult is `{ supplier, branch }` — there is no top-level id.
    onCreated?.(created.supplier.id);
    onClose();
  }

  const disabled = nameTh.trim().length === 0 || branchCode.trim().length === 0 || create.isPending;

  return (
    <AppModal
      title="เพิ่มผู้ขายใหม่"
      subtitle="สร้างผู้ขาย (นิติบุคคล) พร้อมสาขาแรก"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={disabled}
            onClick={() => void handleSubmit()}
          >
            <Icon name="check" size={14} /> {create.isPending ? 'กำลังบันทึก…' : 'บันทึกผู้ขาย'}
          </button>
        </>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div>
          <label className={labelClass} style={labelStyle}>
            ชื่อผู้ขาย (ตามสรรพากร)
          </label>
          <input
            className="input"
            value={nameTh}
            onChange={(e) => setNameTh(e.target.value)}
            placeholder="บริษัท ... จำกัด"
            style={{ width: '100%' }}
          />
        </div>
        <div>
          <label className={labelClass} style={labelStyle}>
            เลขประจำตัวผู้เสียภาษี (13 หลัก)
          </label>
          <input
            className="input"
            value={taxId}
            onChange={(e) => setTaxId(e.target.value)}
            placeholder="0105500000001"
            maxLength={13}
            style={{ width: '100%' }}
          />
        </div>
        <div>
          <label className={labelClass} style={labelStyle}>
            บัญชีค่าใช้จ่ายเริ่มต้น
          </label>
          <SearchSelect
            value={defaultExpenseCode}
            onChange={setDefaultExpenseCode}
            options={expenseOptions}
            getLabel={(v) => expenseLabel.get(v) ?? v}
            placeholder="— ไม่ระบุ —"
          />
        </div>
        <div>
          <label className={labelClass} style={labelStyle}>
            เงื่อนไขชำระ (terms)
          </label>
          <input
            className="input"
            value={terms}
            onChange={(e) => setTerms(e.target.value)}
            placeholder="เช่น N30"
            style={{ width: '100%' }}
          />
        </div>
      </div>

      <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
        <div style={{ fontWeight: 600, marginBottom: 10 }}>สาขาแรก</div>
        <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 14 }}>
          <div>
            <label className={labelClass} style={labelStyle}>
              รหัสสาขา
            </label>
            <input
              className="input"
              value={branchCode}
              onChange={(e) => setBranchCode(e.target.value)}
              maxLength={5}
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <label className={labelClass} style={labelStyle}>
              ชื่อสาขา
            </label>
            <input
              className="input"
              value={branchName}
              onChange={(e) => setBranchName(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
        </div>
        <div style={{ marginTop: 12 }}>
          <label className={labelClass} style={labelStyle}>
            ที่อยู่
          </label>
          <input
            className="input"
            value={addressTh}
            onChange={(e) => setAddressTh(e.target.value)}
            style={{ width: '100%' }}
          />
        </div>
      </div>

      {create.error && (
        <div style={{ color: 'var(--negative)', marginTop: 12 }}>
          บันทึกไม่สำเร็จ: {create.error.message}
        </div>
      )}
    </AppModal>
  );
}
